import { FormSection } from "components/MainSectionTemplate/types";
import React from "react";
import { DaysAfterDateInput, DaysAfterDateText } from "./DaysAfterDateRow";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";

interface Props {
  instruction: server.dto.UpdateClientConfigurationInstruction
}

const reminders = [1, 2, 3];

const hasV1Activated = (instruction: server.dto.UpdateClientConfigurationInstruction) =>
  instruction.invoiceEnabled === "v1AndV2" || instruction.invoiceEnabled === "onlyV1";

const hasV2Activated = (instruction: server.dto.UpdateClientConfigurationInstruction) =>
  instruction.invoiceEnabled === "v1AndV2" || instruction.invoiceEnabled === "onlyV2";

const hasInvoicesActivated = (instruction: server.dto.UpdateClientConfigurationInstruction) => instruction.invoiceEnabled !== "none";

export const InvoiceSection = (props: Props): FormSection => {
  return {
    key: "invoiceSettings",
    type: "regular",
    title: site.currentEnvironment.invoice.singular,

    fields: [{
      key: "invoice_enabled",
      label: RESX.EmbedWizard.Display,
      render: () => <div>{invoiceV2EnabledList.find(option => option.value === props.instruction.invoiceEnabled).label}</div>,
      inPageEdit: {
        render: ({ onChange, value }) => <div className="main-section-template-fields-padding">
          <RadioGroup
            orientation="horizontal"
            options={invoiceV2EnabledList}
            value={value}
            onChange={v => onChange(v, true)}
          />
        </div>,
        type: "confirmOrCancelButton",
        initialValue: props.instruction.invoiceEnabled,
        submitChange: (value: server.dto.InvoiceEnabled) => {
          return window.CoreApi.ClientConfiguration.Update({ ...props.instruction, invoiceEnabled: value });
        }
      }
    },
    {
      key: "invoice_costs",
      label: RESX.Financial.Costs,
      render: () => <YesNoText value={props.instruction.invoiceDisplayCosts} />,
      hidden: !hasV1Activated(props.instruction),
      inPageEdit: {
        render: ({ onChange }) => <YesNoRadioGroupForInPageEditing
          value={props.instruction.invoiceDisplayCosts}
          onChange={(v) => onChange(v, true)}
        />,
        type: "confirmOrCancelButton",
        initialValue: props.instruction.invoiceDisplayCosts,
        submitChange: (value: boolean) => {
          return window.CoreApi.ClientConfiguration.Update({ ...props.instruction, invoiceDisplayCosts: value });
        }
      }
    },
    {
      key: "invoice_draftDefault",
      label: "Draft default",
      render: () => <YesNoText value={props.instruction.legacyInvoiceDefaultDraft} />,
      hidden: !hasV1Activated(props.instruction),
      inPageEdit: {
        render: ({ onChange }) => <YesNoRadioGroupForInPageEditing
          value={props.instruction.legacyInvoiceDefaultDraft}
          onChange={(v) => onChange(v, true)}
        />,
        type: "confirmOrCancelButton",
        initialValue: props.instruction.legacyInvoiceDefaultDraft,
        submitChange: (value: boolean) => {
          return window.CoreApi.ClientConfiguration.Update({ ...props.instruction, legacyInvoiceDefaultDraft: value });
        }
      }
    },
    {
      key: "invoice_dueDate",
      label: RESX.DateTime.DueDate,
      render: () => <DaysAfterDateText days={props.instruction.invoiceDefaultDuration} type="send" />,
      hidden: !hasInvoicesActivated(props.instruction),
      inPageEdit: {
        render: ({ onChange, value, cancel }) => <DaysAfterDateInput
          value={value}
          onChange={onChange}
          maxValue={serverReference.constants.invoice.maxDueDateInterval}
          type="send"
          onEnter={v => onChange(v, true)}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        initialValue: props.instruction.invoiceDefaultDuration,
        submitChange: (value: number) => {
          return window.CoreApi.ClientConfiguration.Update({ ...props.instruction, invoiceDefaultDuration: value });
        }
      }
    },
    ...reminders.map((_, i) => ({
      key: `invoice_reminder_${i + 1}`,
      label: `${RESX.Reminder.xReminder} ${i + 1}`,
      render: () => <DaysAfterDateText days={props.instruction.invoiceReminderInterval[i]} type="due" />,
      hidden: !hasV2Activated(props.instruction),
      inPageEdit: {
        render: ({ onChange, value, cancel }) => <DaysAfterDateInput
          value={value}
          onChange={onChange}
          maxValue={serverReference.constants.invoice.maxReminderInterval}
          type="due"
          onEnter={v => onChange(v, true)}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton" as const,
        initialValue: props.instruction.invoiceReminderInterval[i],
        submitChange: (value: number) => {
          const newIntervals = [...props.instruction.invoiceReminderInterval];
          newIntervals[i] = value;
          return window.CoreApi.ClientConfiguration.Update({ ...props.instruction, invoiceReminderInterval: newIntervals });
        }
      }
    }))
    ]
  }
}

interface InvoiceV2Option {
  value: server.dto.InvoiceEnabled
  label: string
};

const invoiceV2EnabledList: InvoiceV2Option[] = [
  {
    value: "none",
    label: "None"
  },
  {
    value: "onlyV1",
    label: "Only classic workflow (no invoice V2 (BETA))"
  }, {
    value: "v1AndV2",
    label: "Classic worklow and invoice V2 (BETA)"
  }, {
    value: "onlyV2",
    label: "Only invoice V2 (BETA)"
  }
]